import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import OperatorKpis from '../components/OperatorKpis';
import OperatorSubscriptions from '../components/OperatorSubscriptions';
import { operatorColor } from '../utils/theme';
import {
    hasSubscriptionList,
    parseOperatorDetail,
    PRODUCTS,
    SUBSCRIPTIONS_PATH,
} from '../utils/navigation';
import styles from '../styles/Dashboard.module.css';

function BackLink() {
    return (
        <Link to={SUBSCRIPTIONS_PATH} className={styles.backLink}>
            <svg className={styles.backChevron} viewBox="0 0 12 12" aria-hidden="true">
                <path
                    d="M7.5 2.5 4 6l3.5 3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
            Volver al resumen
        </Link>
    );
}

const prefersReducedMotion = () =>
    Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/**
 * Detalle de un operador en un producto: KPIs arriba y la lista de
 * suscripciones debajo, con el filtro de estado compartido entre ambos.
 */
export default function OperatorDetail() {
    const detail = parseOperatorDetail(useParams());
    const [status, setStatus] = useState('');
    const listRef = useRef(null);

    // Una tarjeta de KPI fija el filtro y lleva a la tabla filtrada.
    const filterFromKpi = (value) => {
        setStatus(value);
        listRef.current?.scrollIntoView?.({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    if (!detail) {
        return (
            <div className={styles.page}>
                <BackLink />
                <h1 className={styles.title}>No encontramos esta página</h1>
                <p className={styles.subtitle}>
                    Revisa la dirección o vuelve al resumen para elegir un operador.
                </p>
            </div>
        );
    }

    const product = PRODUCTS[detail.project];

    return (
        <div className={styles.page}>
            <BackLink />

            <header className={styles.detailPageHead}>
                <h1 className={styles.detailPageTitle}>
                    <span
                        className={styles.detailPageDot}
                        style={{ background: operatorColor(detail.operatorName) }}
                    />
                    {detail.operatorName}
                </h1>
                <p className={styles.detailPageProduct}>
                    <img src={product.icon} alt="" className={styles.productIcon} />
                    {product.label}
                </p>
            </header>

            {hasSubscriptionList(detail.operatorName) ? (
                <>
                    <OperatorKpis
                        project={detail.project}
                        operatorName={detail.operatorName}
                        status={status}
                        onStatusChange={filterFromKpi}
                    />
                    <div ref={listRef} className={styles.listAnchor}>
                        <OperatorSubscriptions
                            project={detail.project}
                            operatorName={detail.operatorName}
                            status={status}
                            onStatusChange={setStatus}
                        />
                    </div>
                </>
            ) : (
                <section className={`${styles.tile} ${styles.placeholder}`}>
                    <h2 className={styles.placeholderTitle}>Detalle en preparación</h2>
                    <p className={styles.placeholderText}>
                        Aquí verás las suscripciones de {detail.operatorName} en{' '}
                        {product.label.toLowerCase()} con más detalle. Mientras tanto, sus
                        números están en el resumen.
                    </p>
                </section>
            )}
        </div>
    );
}
